"use client";

import { UserWithCompanies } from "@/components/auth";
import { useAppStore, selectUser } from "@/store/appStore";
import dayjs from "dayjs";
import { FC, useEffect } from "react";
import { Plus } from "lucide-react";
import { AccountExtended, StatementExtended } from "@/types";
import { StatementsProps } from "./statements.types";
import { Content } from "../components/page/components";
import { ContentHeader } from "../components";
import { List } from "../components/list";
import { StatementContextPanel } from "./components";
import IBAN from "iban";
import { CurrencyCode, MenuItem } from "@/components/ui";
import { BankAccount, Statement } from "@prisma/client";
import { StatementContext } from "./context";

const Statements: FC<StatementsProps> = () => {
  const user: UserWithCompanies | null = useAppStore(selectUser);
  const statements = useAppStore((s) => s.statements);
  const accounts = useAppStore((s) => s.bankAccounts);
  const selectedAccount = useAppStore((s) => s.selectedAccount);
  const setSelectedAccount = useAppStore((s) => s.setSelectedAccount);
  const selectedStatement = useAppStore((s) => s.selectedStatement);
  const setSelectedStatement = useAppStore((s) => s.setSelectedStatement);

  return (
    user && (
      <>
        <Content>
          <ContentHeader
            title="Statements"
            subtitle="75% Matched"
            actions={[
              {
                icon: Plus,
                text: "Add Statement",
              },
            ]}
          />
          <List
            groupBy={(account) => account.institution.name}
            sortGroupBy={(a, b) => (a && b ? a.localeCompare(b) : 0)}
            title={(account) => {
              return (
                <MenuItem
                  label={account.institution.name}
                  image={{
                    type: "avatar",
                    source: account.institution.logo,
                    fallback: account.institution.name
                      ? account.institution.name.charAt(0)
                      : "",
                  }}
                  className="flex flex-row items-center"
                />
              );
            }}
            items={accounts.map((account: (typeof accounts)[number]) => {
              const iban = account.iban && IBAN.electronicFormat(account.iban);
              const bban = account.bban && IBAN.electronicFormat(account.bban);
              const statementMatch = statements.filter(
                (statement: Statement) => {
                  return (
                    iban?.includes(
                      IBAN.electronicFormat(statement.accountNumber)
                    ) ||
                    bban?.includes(
                      IBAN.electronicFormat(statement.accountNumber)
                    )
                  );
                }
              )[0];
              return {
                label: account.iban
                  ? IBAN.printFormat(account.iban)
                  : account.bban
                  ? IBAN.printFormat(account.bban)
                  : undefined,
                description: account.currency || undefined,
                isSelected: selectedAccount?.id === account.id,
                status: statementMatch ? "COMPLETED" : "PENDING",
                onClick: () => {
                  setSelectedAccount(account);
                  if (statementMatch) {
                    setSelectedStatement(statementMatch);
                  }
                },
                image: {
                  type: "currency",
                  source: account.currency || undefined,
                  size: "L",
                  fallback: account.institution.name
                    ? account.institution.name.slice(0, 2).toUpperCase()
                    : "N/A",
                },
                institution: account.institution,
              };
            })}
          />
        </Content>
        <StatementContextPanel />
      </>
    )
  );
};

export { Statements };

/*
    const bankAccountsByInstitution: InstitutionWithBankAccounts[] = (user.data?.selectedCompany?.bankAccounts || []).reduce((acc: InstitutionWithBankAccounts[], bankAccount) => {
        let institution = acc.find((i: InstitutionWithBankAccounts) => i.id === bankAccount.institutionId);
        if (!institution) {
          institution = { ...bankAccount.institution, bankAccounts: [] };
          acc.push(institution);
        }
        institution.bankAccounts.push(bankAccount);
        return acc;
      }, []);

   
    const removeStatementMutation = useMutation({
        mutationFn: async (statementId: string) => {
          return await fetch(`/api/statement/remove`, {
            method: 'POST',
            body: JSON.stringify({ statementId }),
          });
        },
        onSuccess: async (response) => {
          const data = await response.json();
          console.log('removeStatementMutation onSuccess', data);
          
    
          queryClient.setQueryData(['user'], 
            {
                ...user.data, 
                selectedReport: {
                  ...user.data?.selectedReport,
                  statements: user.data?.selectedReport?.statements.filter(statement => statement.id !== data.statement.id)
                }
            } as UserWithCompanies)
            }
        
      });
      
    
      const removeStatementHandler = async (statementId: string) => {
        console.log('removeStatementHandler', statementId);
        setStatements(statements.filter(statement => statement.id !== statementId))
        removeStatementMutation.mutate(statementId);
      }*/

/*

    

    return <div className="p-10">
        <h1 className="text-2xl font-bold text-green-700 mb-2">Statements</h1>
        <div className={'space-y-10'}>
            {statements && statements.length ? 
                statements.map((statement: StatementExtended) => <div className="bg-slate-50 border p-5 rounded-sm" key={statement.id}>
                    
                    <div className="flex justify-between">
                        <span className="text-lg font-semibold">{statement.documentName}</span>
                        <a href={statement.documentUrl}>Link</a>

                        <div className="ml-auto">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                    <EllipsisVertical className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    <DropdownMenuItem onClick={() => removeStatementHandler && removeStatementHandler(statement.id)}>
                                    <TrashIcon className="h-4 w-4 mr-2" />
                                    Remove Statement
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            </div>
                    </div>
                    
                    {statement.imagePreviews && statement.imagePreviews.length ?
                        <Image width={600} height={600} src={statement.imagePreviews[0].url} className="" alt="Statement Preview" />
                    : null}

                        <div>
                            <div className="space-x-2 mt-2">
                                <span className="inline-flex items-center rounded-sm bg-white px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">Bank: {statement.bankName}</span>
                                <span className="inline-flex items-center rounded-sm bg-white px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">Account: {statement.accountNumber}</span>
                                <span className="inline-flex items-center rounded-sm bg-white px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"> {statement.currency}</span>
                                <span className="inline-flex items-center rounded-sm bg-white px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10"> {dayjs(statement.startPeriod).format('MMMM, D, YYYY')} - {dayjs(statement.endPeriod).format('MMMM, D, YYYY')}</span>
                            </div>
                        
                            <div className="mt-5 space-y-1">
                                {statement.statementLines?.map((transaction: StatementLine, index: number) => {
                                    return <div key={index} className="flex justify-between border border-gray-200 bg-white py-1 px-2 rounded-sm">
                                    
                                        <span className={`flex content-center space-x-1 items-center text-center justify-items-center ${transaction.type == 'DEBIT' ? 'text-red-600' : ''} ${transaction.type == 'CREDIT' ? 'text-green-700' : ''}`}>
                                            {transaction.type == 'CREDIT' ? <DebitIconComponent /> : null }
                                            {transaction.type == 'DEBIT' ? <CreditIconComponent /> : null }
                                            {transaction.currency && transaction.amount &&
                                                <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: transaction.currency }).format(transaction.amount)}</span>
                                            }
                                        </span>
                                        <span>{transaction.vendorName || transaction.description}</span>
                                        <span>{dayjs(transaction.date).format('MMMM, D, YYYY')}</span>
                                    </div>
                                })}
                            </div>
                        </div>
                </div>)
            : null}
        </div>
    </div>*/
